import Image from 'next/legacy/image'
import { Trans, useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'

import ENV_VARS from '@/config'
import { breakpoints, colors, fontSizes } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import Head from '@/components/head'

import heroBanner from './prompton-condos-and-apartments-for-rent.png'
import promptonLogo from './prompton-logo-main.png'
import promptonQr from './prompton-qrcode.png'

const { BASE_URL } = ENV_VARS

const BannerWrapper = styled.div`
  text-align: center;
  background: url(${({ background }) => background}) 50% 50% no-repeat;
  background-size: cover;
  margin-bottom: 30px;
  height: 600px;
  overflow: hidden;
  position: relative;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    height: auto;
  }
`

const Video = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-height: 100%;
  min-width: 100%;
  z-index: -1;
`

const Vignette = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(20, 20, 20, 0.35);
`

const Container = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  height: 100%;
  width: 100%;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    position: static;
    transform: translate(0, 0);
    padding-left: 25px;
    padding-right: 25px;
    padding-top: 60px;
    padding-bottom: 60px;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1;
`

const HorizontalRule = styled.div`
  width: 380px;
  margin-bottom: 40px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.6);

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 220px;
  }
`

const Heading = styled.h1`
  color: ${colors.white};
  font-size: 20px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin: 0 0 32px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
`

const Description = styled.p`
  color: ${colors.white};
  font-size: 17px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin-bottom: 50px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  small {
    font-size: ${fontSizes.small};
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
`

const DescriptionMobile = styled.p`
  color: ${colors.white};
  font-size: ${fontSizes.small};
  line-height: 22px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin-bottom: 50px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  @media only screen and (min-width: ${breakpoints.tabletPortrait}) {
    display: none;
  }
`

const ContactInfo = styled.p`
  color: ${colors.white};
  font-size: 22px;
  line-height: 30px;
  font-weight: 400;
  letter-spacing: 0.23px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
`

const ContactInfoMobile = styled.p`
  color: ${colors.white};
  font-size: 18px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);

  @media only screen and (min-width: ${breakpoints.tabletPortrait}) {
    display: none;
  }
`

const SectionTitle = styled.h3`
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: bold;
  letter-spacing: -0.49px;
  line-height: 22px;
  text-align: center;
  margin-bottom: 30px;

  @media screen and (max-width: 700px) {
    display: none;
  }
`
const CompanyLogoWrapper = styled.div`
  margin: 0 auto 30px;
  height: 60px;
  width: 320px;
`

const QrCodeTop = css`
  position: absolute;
  top: 845px;
`

const QrCodeBottom = css`
  position: absolute;
  bottom: 20px;
`

const QrCode = styled.div`
  position: fixed;
  bottom: 10px;
  right: 10px;
  z-index: 100;
  border-radius: 5px;
  border: 5px solid ${colors.white};
  background-color: ${colors.white};
  box-shadow: 0 5px 11px 0 rgba(0, 0, 0, 0.23);

  ${props => (props.positionBottom ? QrCodeBottom : null)};
  ${props => (props.positionTop ? null : QrCodeTop)};

  @media screen and (max-width: 890px) {
    display: none;
  }
`

const QrCodeImageWrapper = styled.div`
  position: relative;
  width: 165px;
  height: 210px;
`

const PromptonProfile = ({ footerOnScreen, listingsOnScreen, metadata }) => {
  const { t } = useTranslation('prompton')
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const profile = {
    banner: heroBanner.src,
    title: t(
      'prompton:title.meta',
      'Prompton Real Estate Services - Property Management Vancouver, Toronto'
    ),
    pageDescription: t('prompton:description.meta'),
    url: '/company/prompton'
  }

  return (
    <>
      <Head
        title={profile.title}
        description={profile.pageDescription}
        meta={{
          image: `${BASE_URL}/images/prompton-condos-and-apartments-for-rent.png`,
          imageAlt: t('prompton:Prompton Real Estate Services Inc.')
        }}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <BannerWrapper>
        <Container>
          <Video autoPlay playsInline loop muted>
            <source src="/videos/prompton-banner-video.mp4" type="video/mp4" />
          </Video>
          <Vignette />
          <ContentContainer>
            <CompanyLogoWrapper>
              <Image
                src={promptonLogo}
                alt=""
                width={544}
                height={102}
                placeholder="blur"
              />
            </CompanyLogoWrapper>
            <HorizontalRule />
            <Heading>Prompton Real Estate Services Inc.</Heading>
            <Trans i18nKey="prompton:description.desktop">
              <Description>
                A leader in Real Estate and Property Management for over 30
                years with offices in Vancouver and Toronto. A proud
                <br></br>
                member of the Concord Pacific Group of Companies, the largest
                residential community developer in Canada.
                <br></br>
                <br></br>
                Our team of dedicated, multicultural, highly trained
                professionals who specialize in Real Estate, New-Development and
                <br></br>
                Property Management offer the most comprehensive services in all
                of Greater Vanocuver. Contact us today and learn
                <br></br>
                more about Prompton Real Estate Services.
                <br></br>
                <br></br>
                Property Management | New-Development | Real Estate.
                <br></br>
                <br></br>
              </Description>
            </Trans>
            <Trans i18nKey="prompton:contact.desktop">
              <ContactInfo>
                604-899-2333 | info@prompton.bc.ca | livrent@prompton.bc.ca
                <br></br>
                <br></br>
              </ContactInfo>
            </Trans>
            <Trans i18nKey="prompton:description.mobile">
              <DescriptionMobile>
                A leader in Real Estate and Property Management for over 30
                years with offices in Vancouver and Toronto. A proud member of
                the Concord Pacific Group of Companies, the largest residential
                community developer in Canada.
                <br></br>
                <br></br>
                Our team of dedicated, multicultural, highly trained
                professionals who specialize in Real Estate, New-Development and
                Property Management offer the most comprehensive services in all
                of Greater Vanocuver. Contact us today and learn Professional
                Property Evaluation more about Prompton Real Estate Services.
                <br></br>
                <br></br>
                Property Management | New-Development | Real Estate.
                <br></br>
                <br></br>
              </DescriptionMobile>
            </Trans>
            <Trans i18nKey="prompton:contact.mobile">
              <ContactInfoMobile>
                604-899-2333 | info@prompton.bc.ca | livrent@prompton.bc.ca
              </ContactInfoMobile>
            </Trans>
          </ContentContainer>
        </Container>
      </BannerWrapper>

      <SectionTitle>{t('prompton:Our Portfolio')}</SectionTitle>
      {!displayMobile && (
        <QrCode
          id="qr-code"
          positionBottom={footerOnScreen}
          positionTop={listingsOnScreen}
        >
          <QrCodeImageWrapper>
            <Image
              src={promptonQr}
              alt="Prompton QR Code"
              width={304}
              height={386}
              placeholder="blur"
            />
          </QrCodeImageWrapper>
        </QrCode>
      )}
    </>
  )
}

export default PromptonProfile
